import { FC, ReactNode } from "react";
import { useNavigate } from "react-router-dom";

// Components and Molecules
import HomeHero from "./HomeHero";
import HomePartners from "./HomePartners";
import HomeAbout from "./HomeAbout";
import HomeTestimonials from "./HomeTestimonials";
import HomeLocations from "./HomeLocations";
import HomeFaq from "./HomeFaq";
import HomeBeginJourney from "./HomeBeginJourney";
import Button from "../../../components/Mobile/Button";

import config from "../../../config";

// Images and Icons
import sect1_a from "../../../assets/img/HomePage/Mobile/sect1_a.jpg";
import sect1_b from "../../../assets/img/HomePage/Mobile/sect1_b.jpg";
import sect2_a from "../../../assets/img/HomePage/Mobile/sect2_a.jpg";
import sect2_b from "../../../assets/img/HomePage/Mobile/sect2_b.jpg";

import { ReactComponent as CheckIconFilled } from "../../../icons/CheckIconFilled.svg";

var webUrl = config?.webUrl;

const HomeSections = () => {
  const navigate = useNavigate();

  const bookDemoUrl =
    "https://calendly.com/homecook/homecook-resource-provider-demo";

  return (
    <>
      {/* Hero Section */}
      <HomeHero />

      {/* Section One */}
      <HomeSection
        greybkgColor
        imageBox={{ imageGreyBg: true, imageA: sect1_a, imageB: sect1_b }}
        textBox={{
          subHead: "Food Entrepreneurs",
          mainHead: (
            <>
              <span className="text-primaryDark-400">Expand Beyond</span> Your
              Home Kitchen.
            </>
          ),
          description:
            "Focus on what your culinary dream looks like and let HomeCook take care of the how.",
          points: (
            <>
              <PointBox pointText="Gain access to a network of approved and well-equipped commercial kitchens." />

              <PointBox pointText="Easily find and book kitchen spaces that match your specific needs." />

              <PointBox pointText="Receive guidance on regulatory requirements such as licensing and permits." />

              <PointBox pointText="Stay informed about crucial food safety regulations." />

              <PointBox pointText="Leverage HomeCook’s community features to build valuable connections." />
            </>
          ),
          button: {
            label: "View Resource Pantry",
            onClick: () => {
              window.open(webUrl, "_blank");
            },
            onClickLearnMore: () => {
              navigate("/food-entrepreneurs");
            },
          },
        }}
      />

      {/* Section Two */}
      <HomeSection
        imageBox={{ imageGreyBg: false, imageA: sect2_a, imageB: sect2_b }}
        textBox={{
          subHead: "Resource Providers",
          mainHead: (
            <>
              <span className="text-primaryDark-400">Monetize Your</span> Extra
              Space
            </>
          ),
          description:
            "Monetize your extra kitchen space and support food entrepreneurship in your community.",
          points: (
            <>
              <PointBox pointText="Unlock new income channels by effortlessly connecting with food entrepreneurs seeking commercial kitchen spaces." />

              <PointBox pointText="Support & Access to a platform that helps streamline your kitchen management and rental processes." />

              <PointBox pointText="Leverage a user friendly platform to manage booking acceptances based on preferences." />

              <PointBox pointText="Empower control over community access, including membership approval and status management." />

              <PointBox pointText="Access to HomeCook’s vibrant culinary community." />
            </>
          ),
          button: {
            label: "Book Demo",
            onClick: () => {
              window.open(bookDemoUrl, "_blank");
            },
            onClickLearnMore: () => {
              navigate("/resource-providers");
            },
          },
        }}
      />

      {/* About Section */}
      <HomeAbout />

      {/* Partners Section */}
      <HomePartners />

      {/* Testimonials Section */}
      <HomeTestimonials />

      {/* Locations Section */}
      <HomeLocations />

      {/* FAQ Section */}
      <HomeFaq />

      {/* Begin Journey Section */}
      <HomeBeginJourney />
    </>
  );
};

type HomeSectionProps = {
  greybkgColor?: boolean;
  imageBox: {
    imageGreyBg?: boolean;
    imageA: string;
    imageB: string;
  };
  textBox: {
    subHead: string;
    mainHead: ReactNode;
    description: string;
    points: ReactNode;
    button: {
      label: string;
      onClick: () => void;
      onClickLearnMore: () => void;
    };
  };
};

const HomeSection: FC<HomeSectionProps> = ({
  greybkgColor,
  imageBox,
  textBox,
}) => {
  return (
    <section
      className={`pb-[40px] pt-[60px] ${greybkgColor ? "bg-[#F8F8F8]" : "bg-text-1000"}`}
    >
      {/* Image Box */}
      <div className="w-[361px] relative z-0 mb-[24px] mx-auto">
        {/* Image Background */}
        <div
          className={`w-full h-[651px] ${imageBox.imageGreyBg ? "bg-[#F8F8F8]" : "bg-text-1000"} rounded-[16px] absolute bottom-0`}
        ></div>

        {/* Images */}
        <div className="relative z-[2] bottom-[20px] w-[329px] h-[765px] mx-auto bg-text-1000 rounded-[8px] shadow-md shadow-text-0/24">
          <div className="flex flex-col items-center">
            <img
              src={imageBox.imageA}
              alt="Sect1_imga"
              className="w-[329px] object-cover absolute z-[10] top-0 bg-text-1000"
            />
            <img
              src={imageBox.imageB}
              alt="Sect1_imgb"
              className="w-[313px] mx-auto object-cover absolute z-[3] bottom-[10px] bg-text-1000"
            />
          </div>
        </div>
      </div>

      {/* Text Box */}
      <div className="px-[20px]">
        {/* heading */}
        <div className="mb-[24px]">
          <p className="text-[16px] font-open-sans font-semibold text-primaryDark-0 mb-[8px]">
            {textBox.subHead}
          </p>

          <h3 className="text-[36px] font-raleway font-bold leading-tight text-primaryDark-0 mb-[8px]">
            {textBox.mainHead}
          </h3>

          <p className="text-[16px] text-text-300 font-open-sans leading-loose">
            {textBox.description}
          </p>
        </div>

        {/* Points */}
        <div className="w-full mb-[24px] flex flex-col gap-[16px] flex-shrink-0">
          {textBox.points}
        </div>

        {/* Button */}
        <div className="flex flex-col gap-[16px]">
          <Button
            label={textBox.button.label}
            variant="secondary"
            onClick={textBox.button.onClick}
          />
          <Button
            label="Learn More"
            variant="primary"
            onClick={textBox.button.onClickLearnMore}
          />
        </div>
      </div>
    </section>
  );
};

type PointsBoxProps = {
  pointText: string;
};

const PointBox: FC<PointsBoxProps> = ({ pointText }) => {
  return (
    <div className="flex items-center gap-[8px]">
      <CheckIconFilled className="text-primaryDark-400 w-[20px] h-[20px]" />
      <p className="w-[calc(100%-20px)] text-[16px] font-open-sans font-medium text-text-0 leading-[1.5]">
        {pointText}
      </p>
    </div>
  );
};

export default HomeSections;
